import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  CheckCircle2, FileCheck, Wrench, Award, BarChart3,
  Plus, Check, RotateCcw, X, AlertTriangle, ShieldCheck, Stamp
} from 'lucide-react';
import { formatINR as _fmtINR, formatDate } from '../lib/format';
import { useUrlTab } from '../lib/hooks/useUrlState';

const API = process.env.REACT_APP_BACKEND_URL;

// Closeout uses "—" for null values; non-null uses the standard tight format.
const formatINR = (v) => _fmtINR(v, { dashIfNull: true });

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'punch', label: 'Punch List', icon: CheckCircle2 },
  { id: 'commissioning', label: 'Commissioning', icon: Wrench },
  { id: 'as_built', label: 'As-Built Drawings', icon: FileCheck },
  { id: 'handover', label: 'Handover Certificates', icon: Award },
];

export default function Closeout() {
  const [tab, setTab] = useUrlTab('tab', 'dashboard');
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState('');

  useEffect(() => {
    fetch(`${API}/api/projects`, { credentials: 'include' })
      .then(r => r.json()).then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-5" data-testid="closeout-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-display uppercase tracking-[0.2em] text-slate-500">Project Execution</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">Project Closeout & Handover</h1>
          <p className="text-sm text-slate-500 mt-1">Punch list · Commissioning · As-built drawings · Handover certification — the last mile.</p>
        </div>
        <select value={project} onChange={(e) => setProject(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="closeout-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}
        </select>
      </div>

      <div className="border-b border-slate-200">
        <nav className="flex gap-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`closeout-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-md border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id ? 'text-[#3b4872] border-[#3b4872] bg-indigo-50/40' : 'text-slate-600 border-transparent hover:text-[#0F172A] hover:bg-slate-50'
              }`}>
              <t.icon size={15} />{t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'dashboard' && <Dashboard projectId={project} />}
      {tab === 'punch' && <PunchList projectId={project} projects={projects} />}
      {tab === 'commissioning' && <Commissioning projectId={project} projects={projects} />}
      {tab === 'as_built' && <AsBuilt projectId={project} projects={projects} />}
      {tab === 'handover' && <Handover projectId={project} projects={projects} />}
    </div>
  );
}

// ============================ Dashboard ============================
function Dashboard({ projectId }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    const url = projectId ? `${API}/api/closeout/dashboard?project_id=${projectId}` : `${API}/api/closeout/dashboard`;
    fetch(url, { credentials: 'include' }).then(r => r.json()).then(setD).catch(() => {});
  }, [projectId]);
  if (!d) return <div className="text-center py-12 text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <ProgressKPI label="Punch List Closure" total={d.punch_summary.total} done={d.punch_summary.closed} percent={d.punch_summary.closure_rate} accent="indigo" sub={`${d.punch_summary.open_critical} critical · ${d.punch_summary.open_major} major open`} />
        <ProgressKPI label="Commissioning" total={d.commissioning_summary.total} done={d.commissioning_summary.passed} percent={d.commissioning_summary.pass_rate} accent="emerald" sub={`${d.commissioning_summary.pending} pending · ${d.commissioning_summary.failed} failed`} />
        <ProgressKPI label="As-Built Drawings" total={d.as_built_summary.total} done={d.as_built_summary.approved} percent={d.as_built_summary.approval_rate} accent="blue" sub={`${d.as_built_summary.submitted} submitted · ${d.as_built_summary.pending} pending`} />
        <ProgressKPI label="Handovers Signed" total={d.handover_summary.total || 1} done={d.handover_summary.signed} percent={d.handover_summary.total ? Math.round((d.handover_summary.signed / d.handover_summary.total) * 100) : 0} accent="amber" sub={`${d.handover_summary.pending} pending sign-off`} />
      </div>

      {d.project_readiness && d.project_readiness.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
              <ShieldCheck size={14} /> Handover Readiness — Live Portfolio
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2.5">Project</th>
                    <th className="text-center px-3 py-2.5">Progress</th>
                    <th className="text-center px-3 py-2.5">Open Critical/Major</th>
                    <th className="text-center px-3 py-2.5">Pending Comm</th>
                    <th className="text-center px-3 py-2.5">Pending As-Built</th>
                    <th className="text-center px-3 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {d.project_readiness.map(p => (
                    <tr key={p.id} className="border-b border-slate-100" data-testid={`readiness-${p.code}`}>
                      <td className="px-3 py-2"><div className="font-medium">{p.code}</div><div className="text-xs text-slate-500">{p.name}</div></td>
                      <td className="px-3 py-2 text-center font-bold">{p.progress || 0}%</td>
                      <td className={`px-3 py-2 text-center font-bold ${p.open_critical_punch > 0 ? 'text-red-600' : 'text-emerald-700'}`}>{p.open_critical_punch}</td>
                      <td className={`px-3 py-2 text-center font-bold ${p.pending_commissioning > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{p.pending_commissioning}</td>
                      <td className={`px-3 py-2 text-center font-bold ${p.pending_as_built > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{p.pending_as_built}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${p.ready_for_handover ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                          {p.ready_for_handover ? 'READY' : 'IN PROGRESS'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
            <AlertTriangle size={14} /> Recent Open Punch Items
          </CardTitle>
        </CardHeader>
        <CardContent>
          {d.recent_punch.length === 0 ? <div className="text-sm text-slate-400 py-4 text-center">No open punch items</div> :
            <div className="divide-y divide-slate-100">
              {d.recent_punch.map(p => (
                <div key={p.id} className="py-2.5 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono text-slate-500">{p.punch_no} · {p.location}</div>
                    <div className="text-sm">{p.description}</div>
                  </div>
                  <SeverityBadge severity={p.severity} />
                </div>
              ))}
            </div>
          }
        </CardContent>
      </Card>
    </div>
  );
}

function ProgressKPI({ label, total, done, percent, accent, sub }) {
  const colors = {
    indigo: { bar: 'bg-[#3b4872]', text: 'text-[#3b4872]' },
    emerald: { bar: 'bg-emerald-600', text: 'text-emerald-700' },
    blue: { bar: 'bg-blue-600', text: 'text-blue-700' },
    amber: { bar: 'bg-amber-500', text: 'text-amber-700' },
  }[accent] || { bar: 'bg-slate-500', text: 'text-slate-700' };
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">{label}</div>
        <div className="flex items-baseline gap-2 mt-1">
          <span className={`text-3xl font-display font-extrabold ${colors.text}`}>{percent}%</span>
          <span className="text-xs text-slate-500">{done}/{total}</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-sm mt-2 overflow-hidden">
          <div className={`h-full ${colors.bar} transition-all`} style={{ width: `${percent}%` }} />
        </div>
        {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function SeverityBadge({ severity }) {
  const map = {
    Critical: 'bg-red-50 text-red-700 border-red-200',
    Major: 'bg-amber-50 text-amber-700 border-amber-200',
    Minor: 'bg-blue-50 text-blue-700 border-blue-200',
    Cosmetic: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm border ${map[severity] || 'bg-slate-100 border-slate-200'}`}>{severity}</span>;
}

// ============================ Punch List ============================
function PunchList({ projectId, projects }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [sevFilter, setSevFilter] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    if (statusFilter) params.set('status', statusFilter);
    if (sevFilter) params.set('severity', sevFilter);
    fetch(`${API}/api/closeout/punch-list?${params}`, { credentials: 'include' })
      .then(r => r.json()).then(d => { setItems(d || []); setLoading(false); }).catch(() => setLoading(false));
  }, [projectId, statusFilter, sevFilter]);
  useEffect(load, [load]);

  const close = async (id) => {
    const remarks = prompt('Closure remarks?');
    if (remarks === null) return;
    const r = await fetch(`${API}/api/closeout/punch-list/${id}/close?closure_remarks=${encodeURIComponent(remarks)}`, { method: 'PUT', credentials: 'include' });
    if (r.ok) { toast.success('Punch item closed'); load(); } else toast.error('Failed');
  };
  const reopen = async (id) => {
    const r = await fetch(`${API}/api/closeout/punch-list/${id}/reopen`, { method: 'PUT', credentials: 'include' });
    if (r.ok) { toast.success('Reopened'); load(); } else toast.error('Failed');
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {['', 'Open', 'Closed'].map(s => (
            <button key={s || 'all'} onClick={() => setStatusFilter(s)} data-testid={`punch-status-${s || 'all'}`}
              className={`text-xs px-3 py-1.5 rounded-sm border ${statusFilter === s ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              {s || 'All'}
            </button>
          ))}
          <select value={sevFilter} onChange={(e) => setSevFilter(e.target.value)} className="text-xs border rounded-sm px-2 py-1.5" data-testid="punch-severity-filter">
            <option value="">All severity</option>
            {['Critical', 'Major', 'Minor', 'Cosmetic'].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="punch-new-btn">
          <Plus size={14} className="mr-1.5" />Raise Punch Item
        </Button>
      </div>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="text-left px-3 py-2.5">Punch No</th>
                <th className="text-left px-3 py-2.5">Location</th>
                <th className="text-left px-3 py-2.5">Description</th>
                <th className="text-center px-3 py-2.5">Category</th>
                <th className="text-center px-3 py-2.5">Severity</th>
                <th className="text-left px-3 py-2.5">Against</th>
                <th className="text-center px-3 py-2.5">Status</th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="text-center py-8 text-slate-500">Loading…</td></tr> :
                items.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-slate-500">No punch items.</td></tr> :
                items.map(p => (
                  <tr key={p.id} className="border-b border-slate-100" data-testid={`punch-row-${p.punch_no}`}>
                    <td className="px-3 py-2 font-mono text-xs">{p.punch_no}</td>
                    <td className="px-3 py-2 text-xs">{p.location}</td>
                    <td className="px-3 py-2 max-w-md">{p.description}</td>
                    <td className="px-3 py-2 text-center text-xs"><span className="bg-slate-100 px-2 py-0.5 rounded-sm">{p.category}</span></td>
                    <td className="px-3 py-2 text-center"><SeverityBadge severity={p.severity} /></td>
                    <td className="px-3 py-2 text-xs">{p.raised_against || '—'}</td>
                    <td className="px-3 py-2 text-center"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${p.status === 'Closed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{p.status}</span></td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {p.status === 'Open' ? (
                        <button onClick={() => close(p.id)} className="text-emerald-600 hover:bg-emerald-50 px-2 py-0.5 rounded text-xs border border-emerald-200" data-testid={`punch-close-${p.punch_no}`}>
                          <Check size={12} className="inline mr-1" />Close
                        </button>
                      ) : (
                        <button onClick={() => reopen(p.id)} className="text-amber-700 hover:bg-amber-50 px-2 py-0.5 rounded text-xs border border-amber-200" data-testid={`punch-reopen-${p.punch_no}`}>
                          <RotateCcw size={12} className="inline mr-1" />Reopen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {showForm && <PunchForm projects={projects} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function PunchForm({ projects, onClose, onSaved }) {
  const [form, setForm] = useState({ project_id: projects[0]?.id || '', location: '', description: '', category: 'Civil', severity: 'Minor', raised_against: '', target_close_date: '' });
  const submit = async () => {
    if (!form.project_id || !form.location || !form.description) { toast.error('Project, location & description required'); return; }
    const r = await fetch(`${API}/api/closeout/punch-list`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form),
    });
    if (r.ok) { toast.success('Punch item raised'); onSaved(); } else toast.error('Failed');
  };
  return (
    <Modal title="Raise Punch Item" onClose={onClose} onSubmit={submit} testId="punch-form">
      <Field label="Project"><select value={form.project_id} onChange={(e) => setForm(f => ({ ...f, project_id: e.target.value }))} className={inp}>{projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}</select></Field>
      <Field label="Severity"><select value={form.severity} onChange={(e) => setForm(f => ({ ...f, severity: e.target.value }))} className={inp}>{['Critical', 'Major', 'Minor', 'Cosmetic'].map(s => <option key={s}>{s}</option>)}</select></Field>
      <Field label="Location" full><input value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))} className={inp} placeholder="e.g. Block A · L3 · Boardroom" /></Field>
      <Field label="Category"><select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className={inp}>{['Civil', 'MEP', 'Finishing', 'Structural', 'Façade'].map(c => <option key={c}>{c}</option>)}</select></Field>
      <Field label="Raised Against"><input value={form.raised_against} onChange={(e) => setForm(f => ({ ...f, raised_against: e.target.value }))} className={inp} /></Field>
      <Field label="Target Close Date"><input type="date" value={form.target_close_date} onChange={(e) => setForm(f => ({ ...f, target_close_date: e.target.value }))} className={inp} /></Field>
      <Field label="Description" full><textarea rows={3} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className={inp} /></Field>
    </Modal>
  );
}

// ============================ Commissioning ============================
function Commissioning({ projectId, projects }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const url = projectId ? `${API}/api/closeout/commissioning?project_id=${projectId}` : `${API}/api/closeout/commissioning`;
    fetch(url, { credentials: 'include' }).then(r => r.json()).then(d => { setItems(d || []); setLoading(false); }).catch(() => setLoading(false));
  }, [projectId]);
  useEffect(load, [load]);

  const updateResult = async (id, result) => {
    const params = new URLSearchParams({ result });
    if (result === 'Passed') {
      const cnum = prompt('Clearance certificate number?');
      if (cnum) params.set('clearance_no', cnum);
      const tr = prompt('Test result remarks?');
      if (tr) params.set('test_results', tr);
    } else if (result === 'Failed') {
      const tr = prompt('Failure reason?');
      if (tr) params.set('test_results', tr);
    }
    const r = await fetch(`${API}/api/closeout/commissioning/${id}/result?${params}`, { method: 'PUT', credentials: 'include' });
    if (r.ok) { toast.success('Result recorded'); load(); } else toast.error('Failed');
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="comm-new-btn">
          <Plus size={14} className="mr-1.5" />New Test
        </Button>
      </div>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="text-left px-3 py-2.5">Test No</th>
                <th className="text-left px-3 py-2.5">System</th>
                <th className="text-left px-3 py-2.5">Protocol</th>
                <th className="text-left px-3 py-2.5">Witnessed By</th>
                <th className="text-center px-3 py-2.5">Status</th>
                <th className="text-left px-3 py-2.5">Clearance</th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="text-center py-8 text-slate-500">Loading…</td></tr> :
                items.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-slate-500">No commissioning tests.</td></tr> :
                items.map(c => (
                  <tr key={c.id} className="border-b border-slate-100" data-testid={`comm-row-${c.test_no}`}>
                    <td className="px-3 py-2 font-mono text-xs">{c.test_no}</td>
                    <td className="px-3 py-2"><div className="font-medium">{c.system}</div><div className="text-xs text-slate-500">{c.category}</div></td>
                    <td className="px-3 py-2 text-xs max-w-xs">{c.test_protocol}</td>
                    <td className="px-3 py-2 text-xs">{c.witnessed_by || '—'}</td>
                    <td className="px-3 py-2 text-center"><CommStatus status={c.status} /></td>
                    <td className="px-3 py-2 font-mono text-xs">{c.clearance_no || '—'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {c.status !== 'Passed' && c.status !== 'Failed' && (
                        <select className="text-xs border rounded px-2 py-0.5" value="" onChange={(e) => { const v = e.target.value; if (v) updateResult(c.id, v); }} data-testid={`comm-result-${c.test_no}`}>
                          <option value="">Update…</option>
                          <option>In Progress</option>
                          <option>Passed</option>
                          <option>Failed</option>
                        </select>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {showForm && <CommForm projects={projects} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function CommStatus({ status }) {
  const map = {
    Passed: 'bg-emerald-50 text-emerald-700',
    Failed: 'bg-red-50 text-red-700',
    'In Progress': 'bg-blue-50 text-blue-700',
    Pending: 'bg-amber-50 text-amber-700',
  };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${map[status] || 'bg-slate-100'}`}>{status}</span>;
}

function CommForm({ projects, onClose, onSaved }) {
  const [form, setForm] = useState({ project_id: projects[0]?.id || '', system: '', category: 'MEP', test_protocol: '', witnessed_by: '', target_test_date: '' });
  const submit = async () => {
    if (!form.project_id || !form.system) { toast.error('Project & system required'); return; }
    const r = await fetch(`${API}/api/closeout/commissioning`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form),
    });
    if (r.ok) { toast.success('Test scheduled'); onSaved(); } else toast.error('Failed');
  };
  return (
    <Modal title="New Commissioning Test" onClose={onClose} onSubmit={submit} testId="comm-form">
      <Field label="Project"><select value={form.project_id} onChange={(e) => setForm(f => ({ ...f, project_id: e.target.value }))} className={inp}>{projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}</select></Field>
      <Field label="Category"><select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className={inp}>{['MEP', 'Electrical', 'Civil', 'IT'].map(c => <option key={c}>{c}</option>)}</select></Field>
      <Field label="System" full><input value={form.system} onChange={(e) => setForm(f => ({ ...f, system: e.target.value }))} className={inp} placeholder="e.g. HVAC AHU-3 (Block C)" /></Field>
      <Field label="Test Protocol"><input value={form.test_protocol} onChange={(e) => setForm(f => ({ ...f, test_protocol: e.target.value }))} className={inp} /></Field>
      <Field label="Witnessed By"><input value={form.witnessed_by} onChange={(e) => setForm(f => ({ ...f, witnessed_by: e.target.value }))} className={inp} /></Field>
      <Field label="Target Test Date" full><input type="date" value={form.target_test_date} onChange={(e) => setForm(f => ({ ...f, target_test_date: e.target.value }))} className={inp} /></Field>
    </Modal>
  );
}

// ============================ As-Built ============================
function AsBuilt({ projectId, projects }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const url = projectId ? `${API}/api/closeout/as-built?project_id=${projectId}` : `${API}/api/closeout/as-built`;
    fetch(url, { credentials: 'include' }).then(r => r.json()).then(d => { setItems(d || []); setLoading(false); }).catch(() => setLoading(false));
  }, [projectId]);
  useEffect(load, [load]);

  const updateStatus = async (id, status) => {
    const r = await fetch(`${API}/api/closeout/as-built/${id}/status?status=${encodeURIComponent(status)}`, { method: 'PUT', credentials: 'include' });
    if (r.ok) { toast.success(`Marked ${status}`); load(); } else toast.error('Failed');
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="asb-new-btn">
          <Plus size={14} className="mr-1.5" />Register Drawing
        </Button>
      </div>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="text-left px-3 py-2.5">Drawing No</th>
                <th className="text-left px-3 py-2.5">Title</th>
                <th className="text-center px-3 py-2.5">Discipline</th>
                <th className="text-center px-3 py-2.5">Rev</th>
                <th className="text-left px-3 py-2.5">Consultant</th>
                <th className="text-center px-3 py-2.5">Status</th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="text-center py-8 text-slate-500">Loading…</td></tr> :
                items.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-slate-500">No drawings registered.</td></tr> :
                items.map(a => (
                  <tr key={a.id} className="border-b border-slate-100" data-testid={`asb-row-${a.drawing_no}`}>
                    <td className="px-3 py-2 font-mono text-xs">{a.drawing_no}</td>
                    <td className="px-3 py-2 max-w-md">{a.title}</td>
                    <td className="px-3 py-2 text-center text-xs"><span className="bg-slate-100 px-2 py-0.5 rounded-sm">{a.discipline}</span></td>
                    <td className="px-3 py-2 text-center font-mono text-xs">{a.revision}</td>
                    <td className="px-3 py-2 text-xs">{a.consultant || '—'}</td>
                    <td className="px-3 py-2 text-center"><AsbStatus status={a.status} /></td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <select className="text-xs border rounded px-2 py-0.5" value="" onChange={(e) => { const v = e.target.value; if (v) updateStatus(a.id, v); }} data-testid={`asb-status-${a.drawing_no}`}>
                        <option value="">Move to…</option>
                        {['Draft', 'Submitted', 'Approved', 'Returned'].filter(s => s !== a.status).map(s => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {showForm && <AsbForm projects={projects} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function AsbStatus({ status }) {
  const map = {
    Approved: 'bg-emerald-50 text-emerald-700',
    Submitted: 'bg-blue-50 text-blue-700',
    Returned: 'bg-amber-50 text-amber-700',
    Draft: 'bg-slate-100 text-slate-600',
  };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${map[status] || 'bg-slate-100'}`}>{status}</span>;
}

function AsbForm({ projects, onClose, onSaved }) {
  const [form, setForm] = useState({ project_id: projects[0]?.id || '', drawing_no: '', title: '', discipline: 'Architectural', revision: 'R0', status: 'Draft', consultant: '' });
  const submit = async () => {
    if (!form.project_id || !form.drawing_no || !form.title) { toast.error('Required fields missing'); return; }
    const r = await fetch(`${API}/api/closeout/as-built`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form),
    });
    if (r.ok) { toast.success('Drawing registered'); onSaved(); } else toast.error('Failed');
  };
  return (
    <Modal title="Register As-Built Drawing" onClose={onClose} onSubmit={submit} testId="asb-form">
      <Field label="Project"><select value={form.project_id} onChange={(e) => setForm(f => ({ ...f, project_id: e.target.value }))} className={inp}>{projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}</select></Field>
      <Field label="Discipline"><select value={form.discipline} onChange={(e) => setForm(f => ({ ...f, discipline: e.target.value }))} className={inp}>{['Architectural', 'Structural', 'MEP', 'Plumbing'].map(d => <option key={d}>{d}</option>)}</select></Field>
      <Field label="Drawing No"><input value={form.drawing_no} onChange={(e) => setForm(f => ({ ...f, drawing_no: e.target.value }))} className={inp} /></Field>
      <Field label="Revision"><input value={form.revision} onChange={(e) => setForm(f => ({ ...f, revision: e.target.value }))} className={inp} /></Field>
      <Field label="Title" full><input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} className={inp} /></Field>
      <Field label="Consultant" full><input value={form.consultant} onChange={(e) => setForm(f => ({ ...f, consultant: e.target.value }))} className={inp} /></Field>
    </Modal>
  );
}

// ============================ Handover ============================
function Handover({ projectId, projects }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const url = projectId ? `${API}/api/closeout/handover?project_id=${projectId}` : `${API}/api/closeout/handover`;
    fetch(url, { credentials: 'include' }).then(r => r.json()).then(d => { setItems(d || []); setLoading(false); }).catch(() => setLoading(false));
  }, [projectId]);
  useEffect(load, [load]);

  const sign = async (id) => {
    if (!window.confirm('Sign this handover certificate? Project will be marked Completed if Final.')) return;
    const r = await fetch(`${API}/api/closeout/handover/${id}/sign`, { method: 'PUT', credentials: 'include' });
    if (r.ok) { toast.success('Handover certificate signed'); load(); }
    else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="handover-new-btn">
          <Stamp size={14} className="mr-1.5" />Issue Handover
        </Button>
      </div>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="text-left px-3 py-2.5">Cert No</th>
                <th className="text-left px-3 py-2.5">Project</th>
                <th className="text-center px-3 py-2.5">Type</th>
                <th className="text-right px-3 py-2.5">Contract Value</th>
                <th className="text-right px-3 py-2.5">Retention (5%)</th>
                <th className="text-center px-3 py-2.5">Readiness</th>
                <th className="text-center px-3 py-2.5">Status</th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="text-center py-8 text-slate-500">Loading…</td></tr> :
                items.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-slate-500">No handover certificates issued yet.</td></tr> :
                items.map(h => (
                  <tr key={h.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(h)} data-testid={`handover-row-${h.cert_no}`}>
                    <td className="px-3 py-2 font-mono text-xs">{h.cert_no}</td>
                    <td className="px-3 py-2"><div className="font-medium">{h.project_code}</div><div className="text-xs text-slate-500">{h.project_name}</div></td>
                    <td className="px-3 py-2 text-center"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${h.handover_type === 'Final' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>{h.handover_type}</span></td>
                    <td className="px-3 py-2 text-right">{formatINR(h.contract_value)}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{formatINR(h.retention_value)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${h.readiness?.all_pass ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        {h.readiness?.all_pass ? 'PASS' : `${(h.readiness?.open_critical_punch || 0) + (h.readiness?.pending_commissioning || 0) + (h.readiness?.pending_as_built || 0)} open`}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${h.signed ? 'bg-emerald-50 text-emerald-700' : h.status === 'Ready' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                        {h.signed ? 'SIGNED' : h.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      {!h.signed && h.status === 'Ready' && (
                        <button onClick={() => sign(h.id)} className="text-emerald-600 hover:bg-emerald-50 px-2 py-0.5 rounded text-xs border border-emerald-200" data-testid={`handover-sign-${h.cert_no}`}>
                          <Stamp size={12} className="inline mr-1" />Sign
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {showForm && <HandoverForm projects={projects} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {detail && <HandoverDetailModal cert={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function HandoverForm({ projects, onClose, onSaved }) {
  const [form, setForm] = useState({ project_id: projects[0]?.id || '', handover_type: 'Final', handover_date: new Date().toISOString().slice(0, 10), client_representative: '', contractor_representative: '', notes: '' });
  const submit = async () => {
    if (!form.project_id) { toast.error('Project required'); return; }
    const r = await fetch(`${API}/api/closeout/handover`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form),
    });
    if (r.ok) { toast.success('Handover certificate issued'); onSaved(); } else toast.error('Failed');
  };
  return (
    <Modal title="Issue Handover Certificate" onClose={onClose} onSubmit={submit} testId="handover-form">
      <Field label="Project"><select value={form.project_id} onChange={(e) => setForm(f => ({ ...f, project_id: e.target.value }))} className={inp}>{projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}</select></Field>
      <Field label="Type"><select value={form.handover_type} onChange={(e) => setForm(f => ({ ...f, handover_type: e.target.value }))} className={inp}>{['Substantial', 'Final'].map(t => <option key={t}>{t}</option>)}</select></Field>
      <Field label="Handover Date"><input type="date" value={form.handover_date} onChange={(e) => setForm(f => ({ ...f, handover_date: e.target.value }))} className={inp} /></Field>
      <Field label="Client Representative"><input value={form.client_representative} onChange={(e) => setForm(f => ({ ...f, client_representative: e.target.value }))} className={inp} /></Field>
      <Field label="Contractor Representative" full><input value={form.contractor_representative} onChange={(e) => setForm(f => ({ ...f, contractor_representative: e.target.value }))} className={inp} /></Field>
      <Field label="Notes" full><textarea rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className={inp} /></Field>
    </Modal>
  );
}

function HandoverDetailModal({ cert, onClose }) {
  const r = cert.readiness || {};
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-md w-full max-w-2xl my-6" onClick={e => e.stopPropagation()} data-testid="handover-detail-modal">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <div className="text-[10px] font-display uppercase tracking-[0.18em] text-slate-500">{cert.cert_no} · {cert.handover_type}</div>
            <h3 className="font-display font-bold text-xl">Handover Certificate</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] font-display uppercase tracking-[0.18em] text-slate-500">Project</div>
            <div className="font-bold text-lg">{cert.project_code} · {cert.project_name}</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Detail k="Contract Value" v={formatINR(cert.contract_value)} />
            <Detail k="Retention (5%)" v={formatINR(cert.retention_value)} accent />
            <Detail k="Handover Date" v={cert.handover_date} />
            <Detail k="Type" v={cert.handover_type} />
            <Detail k="Client Rep" v={cert.client_representative} />
            <Detail k="Contractor Rep" v={cert.contractor_representative} />
          </div>
          <div>
            <div className="text-[10px] font-display uppercase tracking-[0.18em] text-slate-500 mb-2">Readiness Checklist</div>
            <div className="space-y-1.5">
              <ChecklistRow ok={r.open_critical_punch === 0} label={`Open Critical/Major punch items: ${r.open_critical_punch || 0}`} />
              <ChecklistRow ok={r.pending_commissioning === 0} label={`Pending commissioning tests: ${r.pending_commissioning || 0}`} />
              <ChecklistRow ok={r.pending_as_built === 0} label={`Pending as-built drawings: ${r.pending_as_built || 0}`} />
            </div>
          </div>
          {cert.notes && <div className="bg-slate-50 p-3 rounded-md text-sm"><div className="text-[10px] font-display uppercase tracking-[0.18em] text-slate-500 mb-1">Notes</div>{cert.notes}</div>}
          {cert.signed && (
            <div className="bg-emerald-50 border-l-4 border-emerald-500 p-3 rounded-md">
              <div className="text-[10px] font-display uppercase tracking-[0.18em] text-emerald-700">Signed</div>
              <div className="text-sm">By <strong>{cert.signed_by}</strong> on {formatDate(cert.signed_at)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChecklistRow({ ok, label }) {
  return (
    <div className={`flex items-center gap-2 text-sm ${ok ? 'text-emerald-700' : 'text-amber-700'}`}>
      {ok ? <Check size={14} /> : <AlertTriangle size={14} />}
      <span>{label}</span>
    </div>
  );
}

function Detail({ k, v, accent }) {
  return (
    <div>
      <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">{k}</div>
      <div className={`text-sm ${accent ? 'font-bold text-[#3b4872]' : 'text-slate-800'}`}>{v || '—'}</div>
    </div>
  );
}

// Shared
const inp = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3b4872]/40";

function Field({ label, children, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="text-[11px] text-slate-600 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, children, onClose, onSubmit, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-md w-full max-w-2xl my-6" onClick={e => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 grid sm:grid-cols-2 gap-3">{children}</div>
        <div className="border-t px-5 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid={`${testId}-submit`}>Save</Button>
        </div>
      </div>
    </div>
  );
}
